import React, { useState, useContext } from "react";
import { Search } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import SearchFieldPicker from "../common/SearchFieldPicker";

const SEARCH_SCOPES = [
  { value: "all", labelKey: "allFields", fallback: "All Fields" },
  { value: "username", labelKey: "username", fallback: "Username" },
  { value: "phone", labelKey: "Phone", fallback: "Phone" },
  { value: "userType", labelKey: "userType", fallback: "User Type" },
  { value: "status", labelKey: "Status", fallback: "Status" },
  { value: "department", labelKey: "department", fallback: "Department" },
  { value: "project", labelKey: "project", fallback: "Project" },
  { value: "group", labelKey: "group", fallback: "Group" },
  { value: "organization", labelKey: "org", fallback: "Organization" },
];

// Type is now filtered via the per-column funnel on the User Type header (see
// UserList.js), so this toolbar only handles free-text search + its scope.
// initialSearchTerm/initialSearchScope let the parent seed a persisted value
// (see UserList.js's userManageTableView storage) back into this component's
// own input state on mount — optional, default to the previous empty/"all"
// behavior so any other caller of this component is unaffected.
// `scopes` lets a caller supply its own field list ([{ value, labelKey, fallback }]);
// defaults to SEARCH_SCOPES above.
const UserToolbar = ({ onSearch, onSearchScopeChange, initialSearchTerm = "", initialSearchScope = "all", scopes = SEARCH_SCOPES }) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [searchScope, setSearchScope] = useState(initialSearchScope);
  const { langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);

  const scopeOptions = scopes.map((s) => ({
    value: s.value,
    label: translations[langMode][s.labelKey] || s.fallback,
  }));
  const currentScopeLabel = scopeOptions.find((s) => s.value === searchScope)?.label;
  const PLACEHOLDER_KEY_BY_SCOPE = { all: "searchAllFields", username: "searchusername", phone: "searchByPhone" };
  // Newer scopes (User Type/Status/Department/Project/Group/Organization) don't
  // have dedicated placeholder translation keys yet — fall back to a generated
  // "Search {label}…" string, same pattern TaskListToolbar already uses for its
  // per-field search placeholders.
  const currentPlaceholder =
    translations[langMode][PLACEHOLDER_KEY_BY_SCOPE[searchScope]] ||
    `${translations[langMode]?.search || "Search"} ${currentScopeLabel || ""}…`;

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    onSearch(term);
  };

  // Changing the search field makes the previously typed term ambiguous (it was
  // typed against a different field), so clear it out rather than silently
  // re-running a stale query against the new scope — matches StockManagement.js's
  // handleSearchScopeChange, which resets its search value(s) the same way.
  const handleScopeSelect = (value) => {
    setSearchScope(value);
    setSearchTerm("");
    onSearchScopeChange?.(value);
    onSearch("");
  };

  return (
    <div className="flex w-full min-w-0 basis-full items-center md:w-auto md:basis-auto">
      {/* Joined button+input pill — same visual/interaction pattern as
          TaskListToolbar's field-picker (src/components/task/TaskListToolbar.js)
          via the shared SearchFieldPicker component, so this dropdown matches
          the rest of the app's list-view field pickers (including click-outside
          to close, rather than the previous close-on-mouse-leave) instead of
          using its own one-off styling. */}
      <SearchFieldPicker
        options={scopeOptions}
        activeValue={searchScope}
        onSelect={handleScopeSelect}
        searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
        isRtl={isRtl}
        buttonTestId="search-scope-button"
      />
      <div className="relative flex min-w-0 flex-1 items-center sm:min-w-[220px] sm:w-[320px]">
        <Search size={15} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${isRtl ? "right-3" : "left-3"}`} />
        <input
          id="account-search"
          type="text"
          autoComplete="off"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={currentPlaceholder}
          className={`w-full h-9 text-sm truncate border border-slate-200 outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${isRtl ? "pr-9 pl-3 border-r-0 rounded-l-lg" : "pl-9 pr-3 border-l-0 rounded-r-lg"}`}
          data-testid="search-input"
        />
      </div>
    </div>
  );
};

export default UserToolbar;
